import type { AuthError } from '@supabase/supabase-js';

export type OAuthProviderId = 'google' | 'azure';

function providerLabel(provider: OAuthProviderId): string {
  switch (provider) {
    case 'google':
      return 'Google';
    case 'azure':
      return 'Microsoft';
    default:
      return 'This provider';
  }
}

/** Some Supabase responses put JSON in `message`; normalize to a single string. */
function flattenMessage(message: string): string {
  const trimmed = message.trim();
  if (!trimmed.startsWith('{')) return trimmed;
  try {
    const j = JSON.parse(trimmed) as { msg?: string; error_description?: string };
    return j.msg ?? j.error_description ?? trimmed;
  } catch {
    return trimmed;
  }
}

/**
 * Turns Supabase OAuth errors into actionable copy (e.g. provider disabled in dashboard).
 */
export function formatOAuthInitError(err: AuthError, provider: OAuthProviderId): Error {
  const text = flattenMessage(err.message ?? '');
  const lower = text.toLowerCase();

  if (
    lower.includes('provider is not enabled') ||
    lower.includes('unsupported provider') ||
    lower.includes('validation_failed')
  ) {
    const label = providerLabel(provider);
    return new Error(
      `${label} sign-in is not enabled for this Supabase project yet. In the Supabase dashboard go to Authentication → Providers, turn on ${label}, and paste your OAuth client ID and secret from Google Cloud or Azure.`
    );
  }

  if (lower.includes('redirect_uri_mismatch') || lower.includes('redirect uri')) {
    return new Error(
      'OAuth redirect URI mismatch. In Microsoft Entra ID → App registrations → your app → Authentication, add this Web redirect URI exactly: https://<your-supabase-project-ref>.supabase.co/auth/v1/callback (use your real project ref from VITE_SUPABASE_URL).'
    );
  }

  return new Error(text || 'Could not start sign-in');
}
